/**
 * useCoinNews Hook
 *
 * React hook for subscribing to coin-specific news via WebSocket.
 * Connects to the news WebSocket, subscribes to a specific coin,
 * and provides real-time news updates.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { WS_BASE_URL } from '../utils/apiConfig';

// WebSocket URL based on environment
const getWsUrl = () => {
  if (typeof window === 'undefined') return null;

  // Check if we're in development mode (Vite dev server)
  const isDev = window.location.port === '5173' || window.location.hostname === 'localhost';

  if (isDev) {
    return WS_BASE_URL ? `${WS_BASE_URL}/news` : `ws://localhost:${window.location.port}/news`;
  }

  // Production: use WS_BASE_URL (api.tradermap.io) or same host
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${WS_BASE_URL || `${protocol}//${window.location.host}`}/news`;
};

/**
 * Format relative time for news articles
 * @param {Date|string} date Article date
 * @returns {string} Formatted relative time
 */
function formatRelativeTime(date) {
  const now = new Date();
  const articleDate = new Date(date);
  const diffMs = now - articleDate;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) {
    return 'Just now';
  } else if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays === 1) {
    return '1 day ago';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return articleDate.toLocaleDateString();
  }
}

/**
 * Process news item for display
 */
function processNewsItem(item) {
  return {
    ...item,
    time: formatRelativeTime(item.pubDate),
    url: item.url?.startsWith('http') ? item.url : `https://${item.url}`
  };
}

/**
 * useCoinNews Hook
 *
 * @param {string} coin Coin symbol (e.g., 'BTC')
 * @param {Object} options Hook options
 * @param {boolean} options.enabled Whether to connect (default: true)
 * @param {number} options.maxNews Maximum news to keep (default: 20)
 * @returns {Object} { news, loading, error, isConnected, refresh }
 */
export function useCoinNews(coin, options = {}) {
  const { enabled = true, maxNews = 20 } = options;

  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectTick, setReconnectTick] = useState(0);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const currentCoinRef = useRef(coin);
  const subscribedCoinRef = useRef(null);

  // Update current coin ref
  currentCoinRef.current = coin;

  // Subscribe to a coin
  const subscribeToCoin = useCallback((coinSymbol) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    const normalizedCoin = coinSymbol.toUpperCase();

    // Unsubscribe from previous coin if different
    if (subscribedCoinRef.current && subscribedCoinRef.current !== normalizedCoin) {
      wsRef.current.send(JSON.stringify({
        type: 'unsubscribe_coin',
        coin: subscribedCoinRef.current
      }));
    }

    // Subscribe to new coin
    wsRef.current.send(JSON.stringify({
      type: 'subscribe_coin',
      coin: normalizedCoin
    }));

    subscribedCoinRef.current = normalizedCoin;
    console.log(`[useCoinNews] Subscribed to ${normalizedCoin}`);
  }, []);

  // Connect to WebSocket (only once)
  useEffect(() => {
    if (!enabled) return;

    mountedRef.current = true;
    const wsUrl = getWsUrl();
    if (!wsUrl) return;

    console.log(`[useCoinNews] Connecting to ${wsUrl}...`);

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) return;

      console.log('[useCoinNews] Connected');
      setIsConnected(true);
      setError(null);

      // Subscribe to current coin
      if (currentCoinRef.current) {
        subscribeToCoin(currentCoinRef.current);
      }
    };

    ws.onmessage = (event) => {
      if (!mountedRef.current) return;

      try {
        const message = JSON.parse(event.data);

        switch (message.type) {
          case 'initial_coin_news':
            // Check if this is for our current coin
            if (message.coin === currentCoinRef.current?.toUpperCase()) {
              const processedNews = (message.data || [])
                .map(processNewsItem)
                .slice(0, maxNews);
              setNews(processedNews);
              setLoading(false);
              console.log(`[useCoinNews] Received ${processedNews.length} news for ${message.coin}`);
            }
            break;

          case 'coin_news':
            // New article - check if it's for our coin
            if (message.data?.coins?.includes(currentCoinRef.current?.toUpperCase())) {
              const processedItem = processNewsItem(message.data);
              setNews(prev => {
                if (prev.some(n => n.guid === processedItem.guid)) {
                  return prev;
                }
                return [processedItem, ...prev].slice(0, maxNews);
              });
            }
            break;

          default:
            break;
        }
      } catch (err) {
        console.error('[useCoinNews] Error parsing message:', err);
      }
    };

    ws.onerror = () => {
      if (!mountedRef.current) return;
      setError('Connection error');
      setIsConnected(false);
    };

    ws.onclose = () => {
      if (!mountedRef.current) return;

      console.log('[useCoinNews] Disconnected');
      setIsConnected(false);
      subscribedCoinRef.current = null;

      // Reconnect after 5 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current && enabled) {
          console.log('[useCoinNews] Reconnecting...');
          setReconnectTick(t => t + 1);
        }
      }, 5000);
    };

    return () => {
      mountedRef.current = false;
      subscribedCoinRef.current = null;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
      wsRef.current = null;
    };
  }, [enabled, maxNews, subscribeToCoin, reconnectTick]);

  // Handle coin change - just resubscribe, don't reconnect
  useEffect(() => {
    if (!coin) return;

    const normalizedCoin = coin.toUpperCase();

    // If we're connected and coin changed, resubscribe
    if (wsRef.current?.readyState === WebSocket.OPEN && subscribedCoinRef.current !== normalizedCoin) {
      setNews([]);
      setLoading(true);
      subscribeToCoin(normalizedCoin);
    }
  }, [coin, subscribeToCoin]);

  // Update relative times every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setNews(prev => prev.map(item => ({
        ...item,
        time: formatRelativeTime(item.pubDate)
      })));
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // Manual refresh
  const refresh = useCallback(() => {
    if (currentCoinRef.current) {
      setNews([]);
      setLoading(true);
      subscribeToCoin(currentCoinRef.current);
    }
  }, [subscribeToCoin]);

  return {
    news,
    loading,
    error,
    isConnected,
    refresh
  };
}

export default useCoinNews;
